import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Plus, X, Truck, Trash2 } from "lucide-react";
import api, { inr, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Overline } from "../components/Reveal";
import { WalletCard } from "./ClothCash";

const TABS = ["orders", "wishlist", "submissions", "cash", "cloth-cash", "referrals", "notifications", "addresses", "profile"];
const TAB_LABELS = { orders: "Orders", wishlist: "Wishlist", submissions: "Submissions", cash: "Cash Received",
  "cloth-cash": "Cloth Cash", referrals: "Referrals", notifications: "Notifications", addresses: "Addresses", profile: "Profile" };

export default function Account() {
  const { user, logout } = useAuth();
  const { wishlist, toggleWishlist, addToCart } = useCart();
  const [params, setParams] = useSearchParams();
  let tab = params.get("tab") || "orders";
  if (tab === "trade-ins") tab = "submissions";
  const [orders, setOrders] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);

  useEffect(() => {
    api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/trade-ins/mine").then((r) => setTradeIns(r.data)).catch(() => {});
  }, []);

  const respond = async (tid, action) => {
    try {
      const { data } = await api.post(`/trade-ins/${tid}/respond`, { action });
      setTradeIns((prev) => prev.map((t) => (t.trade_in_id === tid ? data : t)));
      toast.success(action === "accept" ? "Offer accepted — we'll arrange pickup." : "Offer declined.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20" data-testid="account-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Overline>My Account</Overline>
          <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter mt-3">{user.name}</h1>
          <p className="mt-2 text-sm text-muted-foreground">{user.email} · {user.phone}</p>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="text-[11px] tracking-[0.25em] uppercase border border-ink px-5 py-3 hover:bg-ink hover:text-ivory transition-colors">Sign Out</button>
      </div>

      <div className="mt-12 flex gap-1 border-b border-border overflow-x-auto" data-testid="account-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setParams({ tab: t }, { replace: true })} data-testid={`tab-${t}`}
            className={`px-5 py-3.5 text-[11px] tracking-[0.2em] uppercase whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === t ? "border-wine text-wine" : "border-transparent text-muted-foreground hover:text-ink"}`}>
            {TAB_LABELS[t] || t.replace("-", " ")}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {tab === "orders" && (
          orders.length === 0 ? <Empty text="No orders yet." cta="Shop Collection" to="/shop" testid="orders-empty" /> : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((o) => (
                <div key={o.order_id} className="border border-border p-6 flex flex-wrap justify-between gap-4" data-testid={`order-${o.order_id}`}>
                  <div>
                    <p className="font-mono text-sm">{o.order_id}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleDateString("en-IN")} · {o.items.length} item(s)</p>
                    <p className="text-sm mt-2 text-ink/60">{o.items.map((i) => i.name).join(", ")}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-lg">{inr(o.total)}</p>
                    {o.cloth_cash_used > 0 && <p className="text-xs text-gold font-mono">− {inr(o.cloth_cash_used)} Cloth Cash</p>}
                    <p className={`mt-1 text-[10px] tracking-[0.2em] uppercase ${o.status === "paid" ? "text-gold" : "text-muted-foreground"}`}>{o.status.replace(/_/g, " ")}</p>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "wishlist" && (
          wishlist.length === 0 ? <Empty text="Your wishlist is empty." cta="Discover Pieces" to="/shop" testid="wishlist-empty" /> : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-12" data-testid="wishlist-grid">
              {wishlist.map((p) => (
                <div key={p.product_id} className="group" data-testid={`wishlist-item-${p.product_id}`}>
                  <Link to={`/product/${p.product_id}`} className="block aspect-[3/4] bg-sand overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </Link>
                  <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-muted-foreground">{p.brand}</p>
                  <p className="font-serif text-lg">{p.name}</p>
                  <p className="font-mono text-sm mt-1">{inr(p.price)}</p>
                  <div className="mt-3 flex gap-2">
                    <button onClick={() => addToCart(p)} data-testid={`wishlist-add-cart-${p.product_id}`} className="flex-1 bg-ink text-ivory py-2.5 text-[10px] tracking-[0.2em] uppercase">Add to Cart</button>
                    <button onClick={() => toggleWishlist(p)} data-testid={`wishlist-remove-${p.product_id}`} className="px-3 border border-border text-[10px] tracking-[0.2em] uppercase">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {tab === "submissions" && (
          tradeIns.length === 0 ? <Empty text="No submissions yet. Your closet has value." cta="Sell Your Clothes" to="/sell" testid="submissions-empty" /> : (
            <div className="space-y-4" data-testid="tradeins-list">
              {tradeIns.map((t) => (
                <div key={t.trade_in_id} className="border border-border p-6" data-testid={`tradein-${t.trade_in_id}`}>
                  <div className="flex flex-wrap justify-between gap-4">
                    <div>
                      <p className="font-mono text-sm">{t.trade_in_id}</p>
                      <p className="font-serif text-xl mt-1">{t.brand_name} · {t.item_type}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Submitted {new Date(t.created_at).toLocaleDateString("en-IN")} · Condition: {t.condition}
                        {t.completed_at && ` · Completed ${new Date(t.completed_at).toLocaleDateString("en-IN")}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 ${t.status === "Trade-In Completed" ? "bg-gold text-ink" : t.status === "Rejected" ? "bg-destructive text-ivory" : "bg-sand text-ink"}`} data-testid={`tradein-status-${t.trade_in_id}`}>{t.status === "Cloth Cash Offer Ready" ? "Offer Ready" : t.status}</span>
                      {t.classification && <p className="mt-1.5 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Classification: <span className="text-wine capitalize">{t.classification}</span></p>}
                      {t.offer != null && (
                        <div className="mt-2">
                          {t.payout_type === "cash" ? (
                            <p className="font-mono text-sm">Cash Offer: <span className="text-gold">{inr(t.offer)}</span></p>
                          ) : (
                            <>
                              {t.approved_value != null && <p className="font-mono text-xs text-muted-foreground">Approved Trade-In Value: {inr(t.approved_value)}</p>}
                              <p className="font-mono text-sm">Your Cloth Cash: <span className="text-gold">{inr(t.offer)}</span></p>
                            </>
                          )}
                        </div>
                      )}
                      {t.cash_paid && <p className="font-mono text-sm text-gold">Cash Paid: {inr(t.cash_amount)}</p>}
                      {t.final_cloth_cash != null && <p className="font-mono text-sm text-gold">Issued: {inr(t.final_cloth_cash)}</p>}
                    </div>
                  </div>
                  {t.photos?.length > 0 && (
                    <div className="mt-4 flex gap-2 overflow-x-auto">
                      {t.photos.map((ph, i) => <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${ph}`} alt="" className="w-16 h-20 object-cover" />)}
                    </div>
                  )}
                  {t.status === "Cloth Cash Offer Ready" && (
                    <div className="mt-5 flex gap-3 border-t border-border pt-5">
                      <button onClick={() => respond(t.trade_in_id, "accept")} data-testid={`accept-offer-${t.trade_in_id}`}
                        className="bg-wine text-ivory px-6 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-ink transition-colors">Accept Offer</button>
                      <button onClick={() => respond(t.trade_in_id, "decline")} data-testid={`decline-offer-${t.trade_in_id}`}
                        className="border border-ink px-6 py-3 text-[11px] tracking-[0.2em] uppercase hover:bg-ink hover:text-ivory transition-colors">Decline Offer</button>
                    </div>
                  )}
                  <TradeInActions t={t} onUpdate={(data) => setTradeIns((prev) => prev.map((x) => (x.trade_in_id === t.trade_in_id ? data : x)))} />
                </div>
              ))}
            </div>
          )
        )}

        {tab === "cloth-cash" && <WalletCard />}

        {tab === "cash" && (
          <div data-testid="cash-tab">
            {tradeIns.filter((t) => t.payout_type === "cash").length === 0 ? (
              <Empty text="No cash payouts yet." cta="Sell Your Clothes" to="/sell" testid="cash-empty" />
            ) : (
              <div className="space-y-4">
                {tradeIns.filter((t) => t.payout_type === "cash").map((t) => (
                  <div key={t.trade_in_id} className="border border-border p-6 flex flex-wrap justify-between gap-4" data-testid={`cash-${t.trade_in_id}`}>
                    <div>
                      <p className="font-mono text-sm">{t.trade_in_id}</p>
                      <p className="font-serif text-xl mt-1">{t.brand_name} · {t.item_type}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(t.created_at).toLocaleDateString("en-IN")}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-lg">{inr(t.cash_amount)}</p>
                      <p className={`mt-1 text-[10px] tracking-[0.2em] uppercase ${t.cash_paid ? "text-gold" : "text-muted-foreground"}`}>
                        {t.cash_paid ? `Paid ${new Date(t.cash_paid_at).toLocaleDateString("en-IN")}` : "Pending pickup inspection"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "notifications" && <Notifications tradeIns={tradeIns} />}

        {tab === "addresses" && <Addresses />}

        {tab === "referrals" && <Referrals />}

        {tab === "profile" && (
          <div className="max-w-md border border-border p-8" data-testid="profile-card">
            {[["Name", user.name], ["Email", user.email], ["Phone", user.phone], ["Member Since", new Date(user.created_at).toLocaleDateString("en-IN")]].map(([k, v]) => (
              <div key={k} className="flex justify-between py-3 border-b border-border last:border-0 text-sm">
                <span className="text-muted-foreground">{k}</span><span>{v}</span>
              </div>
            ))}
            <p className="mt-6 text-[11px] text-muted-foreground">Saved addresses and notifications coming soon.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function Notifications({ tradeIns }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/notifications/mine").then((r) => setData(r.data)).catch(() => setData({ notifications: [], unread: 0 }));
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="max-w-2xl" data-testid="notifications-tab">
      {data.notifications.length > 0 && (
        <div className="mb-5 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{data.unread} unread</p>
          <button onClick={markAll} data-testid="notif-read-all" className="text-[10px] tracking-[0.2em] uppercase text-wine hover:text-ink">Mark all read</button>
        </div>
      )}
      {data.notifications.length === 0 ? (
        <div className="border-t border-border">
          {tradeIns.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">No notifications yet. Submission updates will appear here.</p>
          ) : tradeIns.map((t) => (
            <div key={t.trade_in_id} className="py-4 border-b border-border flex justify-between gap-4 text-sm" data-testid={`notif-${t.trade_in_id}`}>
              <div>
                <p className="font-medium">{t.status === "Cloth Cash Offer Ready" ? `Your offer is ready — ${inr(t.offer)}` : `Submission ${t.trade_in_id} is now “${t.status}”`}</p>
                <p className="text-xs text-muted-foreground mt-1">{t.brand_name} {t.item_type} · {t.trade_in_id}</p>
              </div>
              <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{new Date(t.updated_at || t.created_at).toLocaleDateString("en-IN")}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="border-t border-border">
          {data.notifications.map((n) => (
            <div key={n.id} className={`py-4 border-b border-border flex justify-between gap-4 text-sm ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id}`}>
              <div className="flex gap-3">
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-wine shrink-0" />}
                <div>
                  <p className={n.read ? "" : "font-medium"}>{n.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.channel === "whatsapp" ? "Sent via WhatsApp + " : ""}In-app · {n.event.replace(/_/g, " ")}</p>
                </div>
              </div>
              <span className="font-mono text-xs text-muted-foreground whitespace-nowrap">{new Date(n.created_at).toLocaleDateString("en-IN")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TradeInActions({ t, onUpdate }) {
  const [photos, setPhotos] = useState([]);
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const SLOTS = ["10 AM – 1 PM", "1 PM – 4 PM", "4 PM – 7 PM"];
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(); d.setDate(d.getDate() + i + 1);
    return d.toISOString().slice(0, 10);
  });

  const readPhoto = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Photo must be under 5MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhotos((p) => [...p, reader.result]);
    reader.readAsDataURL(file);
  };

  const uploadPhotos = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trade-ins/${t.trade_in_id}/photos`, { photos });
      onUpdate(data); setPhotos([]);
      toast.success("Photos added — your Trade-In is back under review");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const schedule = async () => {
    if (!date || !slot) { toast.error("Choose a date and time slot"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/trade-ins/${t.trade_in_id}/schedule-pickup`, { date, slot });
      onUpdate(data);
      toast.success("Pickup scheduled");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      {t.status === "More Information Required" && (
        <div className="mt-5 border-t border-border pt-5" data-testid={`reupload-${t.trade_in_id}`}>
          <p className="text-sm text-ink/70 flex items-center gap-2"><Camera size={15} strokeWidth={1.5} /> We've requested more photographs of this item.</p>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden"
            onChange={(e) => [...e.target.files].forEach(readPhoto)} data-testid={`reupload-input-${t.trade_in_id}`} />
          <div className="mt-3 flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <div key={i} className="relative w-16 h-20 group">
                <img src={p} alt="" className="w-full h-full object-cover" />
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="absolute -top-1.5 -right-1.5 bg-ink text-ivory rounded-full p-0.5" aria-label="Remove"><X size={10} /></button>
              </div>
            ))}
            <button onClick={() => fileRef.current?.click()} data-testid={`reupload-add-${t.trade_in_id}`}
              className="w-16 h-20 border border-dashed border-border hover:border-wine flex flex-col items-center justify-center gap-1 text-muted-foreground">
              <Plus size={14} strokeWidth={1.5} /><span className="text-[8px] tracking-[0.15em] uppercase">Add</span>
            </button>
          </div>
          {photos.length > 0 && (
            <button onClick={uploadPhotos} disabled={busy} data-testid={`reupload-submit-${t.trade_in_id}`}
              className="mt-3 bg-wine text-ivory px-6 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-ink transition-colors disabled:opacity-50">
              {busy ? "Uploading…" : `Submit ${photos.length} Photo${photos.length > 1 ? "s" : ""}`}
            </button>
          )}
        </div>
      )}
      {t.status === "Customer Accepted" && (
        <div className="mt-5 border-t border-border pt-5" data-testid={`pickup-${t.trade_in_id}`}>
          <p className="text-sm text-ink/70 flex items-center gap-2"><Truck size={15} strokeWidth={1.5} /> Offer accepted — choose your doorstep pickup slot.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {days.map((d) => (
              <button key={d} onClick={() => setDate(d)} data-testid={`pickup-date-${d}`}
                className={`px-3 py-2.5 border text-xs transition-colors ${date === d ? "bg-ink text-ivory border-ink" : "border-border hover:border-ink"}`}>
                {new Date(d + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}
              </button>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {SLOTS.map((s) => (
              <button key={s} onClick={() => setSlot(s)} data-testid={`pickup-slot-${s.replace(/[^a-z0-9]/gi, "-")}`}
                className={`px-3 py-2.5 border text-xs transition-colors ${slot === s ? "bg-wine text-ivory border-wine" : "border-border hover:border-ink"}`}>{s}</button>
            ))}
          </div>
          <button onClick={schedule} disabled={busy || !date || !slot} data-testid={`pickup-confirm-${t.trade_in_id}`}
            className="mt-3 bg-ink text-ivory px-6 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-wine transition-colors disabled:opacity-40">
            {busy ? "Scheduling…" : "Confirm Pickup"}
          </button>
        </div>
      )}
      {t.pickup_date && (
        <p className="mt-4 text-xs text-muted-foreground" data-testid={`pickup-info-${t.trade_in_id}`}>
          Pickup: {new Date(t.pickup_date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} · {t.pickup_slot}
        </p>
      )}
      {(t.collection_status || t.pickup_agent?.name) && (
        <p className="mt-2 text-xs text-muted-foreground" data-testid={`collection-info-${t.trade_in_id}`}>
          {t.collection_status && <>Collection: <span className="text-wine">{t.collection_status}</span></>}
          {t.pickup_agent?.name && <> · Agent: {t.pickup_agent.name} ({t.pickup_agent.phone})</>}
        </p>
      )}
      {t.offer_expired && (
        <p className="mt-2 text-xs text-wine" data-testid={`offer-expired-${t.trade_in_id}`}>Your previous offer expired unanswered — our team is re-evaluating.</p>
      )}
    </>
  );
}

function Addresses() {
  const [addresses, setAddresses] = useState(null);
  const load = () => api.get("/account/addresses").then((r) => setAddresses(r.data)).catch(() => setAddresses([]));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    try { const { data } = await api.delete(`/account/addresses/${id}`); setAddresses(data); toast.success("Address removed"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  if (addresses === null) return null;
  return (
    <div className="max-w-2xl" data-testid="addresses-tab">
      {addresses.length === 0 ? (
        <div className="border border-border p-8 text-center">
          <p className="font-serif text-2xl">No saved addresses.</p>
          <p className="mt-2 text-sm text-muted-foreground">Check "Save this address" at checkout and it will appear here.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.id} className="border border-border p-5 flex justify-between gap-4" data-testid={`address-${a.id}`}>
              <div className="text-sm">
                <p className="text-[10px] tracking-[0.2em] uppercase text-gold">{a.label}</p>
                <p className="mt-2">{a.line1}</p>
                <p className="text-muted-foreground">{a.city}{a.city && a.state ? ", " : ""}{a.state} {a.pincode}</p>
              </div>
              <button onClick={() => remove(a.id)} data-testid={`address-delete-${a.id}`} aria-label="Delete address" className="text-muted-foreground hover:text-destructive self-start"><Trash2 size={15} strokeWidth={1.5} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Referrals() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/referrals/mine").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const link = `${window.location.origin}/auth?ref=${data.code}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success("Referral link copied"); };
  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/referrals/apply", { code });
      toast.success("Referral code applied");
      setCode("");
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-2xl" data-testid="referrals-tab">
      <div className="bg-ink text-ivory p-8 md:p-10">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold">Refer &amp; Earn</p>
        <p className="mt-3 font-serif text-2xl md:text-3xl leading-snug">Give a friend a smarter closet. When their first Trade-In completes, you earn <span className="text-gold font-mono">{inr(data.reward)}</span> Cloth Cash.</p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <span className="font-mono text-2xl tracking-[0.2em] bg-ivory/10 px-5 py-3" data-testid="referral-code">{data.code}</span>
          <button onClick={copy} data-testid="referral-copy" className="border border-gold text-gold px-5 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-gold hover:text-ink transition-colors">Copy Link</button>
        </div>
        <p className="mt-3 text-xs text-ivory/40 font-mono break-all">{link}</p>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-px bg-border border border-border">
        <div className="bg-ivory p-6"><p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Total Earned</p><p className="mt-2 font-mono text-2xl text-gold" data-testid="referral-earned">{inr(data.total_earned)}</p></div>
        <div className="bg-ivory p-6"><p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Friends Referred</p><p className="mt-2 font-mono text-2xl">{data.referrals.length}</p></div>
      </div>
      {data.referrals.length > 0 && (
        <div className="mt-6 border-t border-border" data-testid="referral-list">
          {data.referrals.map((r, i) => (
            <div key={i} className="py-4 border-b border-border flex justify-between items-center text-sm">
              <div>
                <p>{r.name}</p>
                <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-IN")}</p>
              </div>
              <span className={`text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 ${r.status === "rewarded" ? "bg-gold text-ink" : "bg-sand text-ink/60"}`}>
                {r.status === "rewarded" ? `+${inr(r.reward)} earned` : "Pending first Trade-In"}
              </span>
            </div>
          ))}
        </div>
      )}
      {!data.referred_by && (
        <form onSubmit={apply} className="mt-8 border border-border p-6" data-testid="apply-referral">
          <p className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Have a friend's code?</p>
          <div className="mt-3 flex gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ENTER CODE" data-testid="apply-referral-input"
              className="flex-1 bg-transparent border border-border px-4 py-3 text-sm font-mono tracking-widest outline-none focus:border-wine" />
            <button type="submit" disabled={busy || !code} data-testid="apply-referral-btn"
              className="bg-ink text-ivory px-6 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-wine transition-colors disabled:opacity-40">Apply</button>
          </div>
        </form>
      )}
      {data.referred_by && <p className="mt-8 text-xs text-muted-foreground">You joined with referral code <span className="font-mono">{data.referred_by}</span>.</p>}
    </div>
  );
}

function Empty({ text, cta, to, testid }) {
  return (
    <div className="py-20 text-center" data-testid={testid}>
      <p className="font-serif text-3xl text-muted-foreground">{text}</p>
      <Link to={to} className="inline-block mt-8 bg-ink text-ivory px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-wine transition-colors">{cta}</Link>
    </div>
  );
}
